import SettingsSection from '@/pages/settings/components/SettingsSection';
import { ShopifyIcon } from '@/components/icons';
import { usePaywall } from '@/hooks/use-paywall';
import { Button, Stack, Text } from '@chakra-ui/react';
import type { OrganizationBillingContext } from '@fluenceur/types';
import { NoticePanel } from '@fluenceur/ui';
import { useTranslation } from 'react-i18next';

/** The financial settings stay readable while the same plan choice awaits approval. */
export function ShopifyFreeBillingSection({
  context,
  mutationsDisabled = false,
}: {
  context: OrganizationBillingContext;
  mutationsDisabled?: boolean;
}) {
  const { t } = useTranslation();
  const { openPlans } = usePaywall();
  const approval = context.shopifyFreeApproval;
  if (!approval || approval.requirement === 'not_required') return null;
  const needsApproval = approval.requirement !== 'approved';
  return (
    <SettingsSection
      title={t('shopifyFreeApproval.billingTitle')}
      description={t('shopifyFreeApproval.summary')}
    >
      <Stack gap={4}>
        {needsApproval && (
          <NoticePanel tone="info" title={t('shopifyFreeApproval.title')}>
            <Stack gap={3}>
              <Text>
                {t(
                  approval.canApprove
                    ? 'shopifyFreeApproval.body'
                    : 'shopifyFreeApproval.memberBody'
                )}
              </Text>
              {approval.canApprove && (
                <Button
                  variant="solid"
                  size="sm"
                  bg="studio.info.fg"
                  color="white"
                  _hover={{ bg: 'studio.info.fg/90' }}
                  alignSelf="flex-start"
                  maxW="full"
                  disabled={mutationsDisabled}
                  whiteSpace="normal"
                  h="auto"
                  minH={8}
                  py={2}
                  onClick={() => openPlans('free')}
                >
                  {t('shopifyFreeApproval.approve')}
                </Button>
              )}
            </Stack>
          </NoticePanel>
        )}
        <Text color="inkMuted">{t('shopifyFreeApproval.financialAccess')}</Text>
        <Button asChild variant="outline" alignSelf="flex-start">
          <a
            href="https://admin.shopify.com/settings/billing"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ShopifyIcon boxSize="19px" flex="none" aria-hidden />
            {t('settings.subscription.billingManagement.shopifyInvoices')}
          </a>
        </Button>
      </Stack>
    </SettingsSection>
  );
}
